import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DestinyTeamOption, TeamPublic, UserPublic } from '@mondy/shared';
import { getTeamTheme, PAYMENT_TIERS } from '@mondy/shared';
import { api } from '@/api';
import { ApiError } from '@mondy/shared/api-client';
import { cn } from '@/lib/cn';
import { COMMON_TIMEZONES } from '@/lib/timezones';
import { localTimezone } from '@/lib/format';
import { useCurrentUser, useLogout } from '@/hooks/useCurrentUser';
import { useFavoriteTeamTheme } from '@/hooks/useFavoriteTeamTheme';
import { Spinner } from '@/components/Spinner';
import { ConfirmModal } from '@/components/ConfirmModal';
import type { StandingsRow } from '@/components/MatchStandings';
import { GroupsCard } from '@/components/GroupsCard';
import { TeamBackdrop } from '@/components/TeamBackdrop';
import { TierBadge } from '@/components/TierBadge';
import { useActiveGroup } from '@/state/activeGroup';
import { useMyGroups } from '@/hooks/useMyGroups';
import pkg from '../../package.json';

interface TeamsResponse {
  teams: TeamPublic[];
}

interface StandingsResponse {
  standings: StandingsRow[];
}

const inputBase =
  'h-11 w-full rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 text-base text-[color:var(--color-fg)] outline-none focus:border-[color:var(--color-accent)]';

export function ProfilePage() {
  const user = useCurrentUser();
  const logout = useLogout();
  const navigate = useNavigate();
  const { theme: favoriteTheme } = useFavoriteTeamTheme();

  if (!user) return null;

  return (
    <>
      <div className="page-head">
        <div className="page-title">
          <div className="k">{user.email}</div>
          <h1
            style={
              favoriteTheme
                ? { fontFamily: 'var(--team-font)', letterSpacing: '0.01em' }
                : undefined
            }
          >
            <span
              className="emph"
              style={favoriteTheme ? { color: favoriteTheme.palette.primary } : undefined}
            >
              {user.displayName}
            </span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                marginLeft: 6,
                verticalAlign: 'middle',
              }}
            >
              <TierBadge tier={user.paymentTier} size="md" />
            </span>
          </h1>
        </div>
      </div>
      <div className="flex w-full max-w-2xl flex-col gap-6">
        {user.role === 'admin' ? <AdminShortcutsCard /> : null}
        {user.role === 'admin' ? <PaymentTierCard user={user} /> : null}

        <FavoriteTeamCard />

        <GroupsCard />

        <StatsCard user={user} />
        <DisplayNameCard current={user.displayName} />

        <button
          type="button"
          onClick={() => {
            logout();
            navigate('/login');
          }}
          className="h-11 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] font-medium"
        >
          Log out
        </button>

        <p className="text-center text-xs text-[color:var(--color-fg-muted)] tabular-nums">
          v{pkg.version}
        </p>
      </div>
    </>
  );
}

function Card({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
      <header className="flex flex-col gap-0.5">
        <h2 className="text-base font-semibold">{title}</h2>
        {description ? (
          <p className="text-xs text-[color:var(--color-fg-muted)]">{description}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}

/**
 * Admin shortcuts. Appears ONLY for admins and sits above the stats card
 * so that admins on mobile (where the Admin tab is no longer in the
 * bottom bar) still have a one-tap entry to the admin console. Desktop
 * admins also get the sidebar link — this is the mobile-first path.
 */
function AdminShortcutsCard() {
  const links: { to: string; label: string; icon: string }[] = [
    { to: '/admin/matches', label: 'Match results', icon: '⚽' },
    { to: '/admin/users', label: 'Users', icon: '👥' },
    { to: '/admin/settings', label: 'Settings', icon: '⚙' },
  ];
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-[color:var(--color-accent)]/40 bg-[color:var(--color-accent)]/10 p-4">
      <header className="flex items-center gap-2">
        <span aria-hidden>⚙</span>
        <h2 className="text-base font-semibold">Admin console</h2>
      </header>
      <div className="flex flex-col gap-2 sm:flex-row">
        {links.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 py-3 text-sm font-medium active:opacity-80"
          >
            <span aria-hidden>{l.icon}</span>
            <span>{l.label}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function PaymentTierCard({ user }: { user: UserPublic }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<(typeof PAYMENT_TIERS)[number]>(user.paymentTier);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    setSelected(user.paymentTier);
  }, [user.paymentTier]);

  const save = useMutation({
    mutationFn: () =>
      api.put<{ user: UserPublic }>('/api/user/payment-tier', { tier: selected }),
    onSuccess: () => {
      setError(null);
      setOk(true);
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Save failed'),
  });

  const dirty = selected !== user.paymentTier;

  return (
    <Card
      title="Payment tier"
      description="Admin override — sets the stored tier directly. Stripe webhook can still upgrade this on a future paid event."
    >
      <div className="flex flex-col gap-3">
        <select
          value={selected}
          onChange={(e) => {
            setSelected(e.target.value as (typeof PAYMENT_TIERS)[number]);
            setOk(false);
          }}
          className={cn(inputBase, 'appearance-none')}
        >
          {PAYMENT_TIERS.map((tier) => (
            <option key={tier} value={tier}>
              {tier.charAt(0).toUpperCase() + tier.slice(1)}
            </option>
          ))}
        </select>
        {error ? <p className="text-xs text-[color:var(--color-danger)]">{error}</p> : null}
        {ok ? <p className="text-xs text-[color:var(--color-accent)]">Saved.</p> : null}
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
          className="h-11 rounded-xl bg-[color:var(--color-accent)] font-medium text-[color:var(--color-accent-fg)] disabled:pointer-events-none disabled:opacity-60"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Card>
  );
}

function StatsCard({ user }: { user: UserPublic }) {
  const activeGroupId = useActiveGroup();
  const groupsQuery = useMyGroups();
  const hasGroups = (groupsQuery.data?.groups.length ?? 0) > 0;

  const standings = useQuery({
    queryKey: ['standings', 'overall', activeGroupId],
    queryFn: () =>
      api.get<StandingsResponse>(`/api/standings?groupId=${encodeURIComponent(activeGroupId)}`),
    enabled: hasGroups,
  });

  const myRow = useMemo(() => {
    if (!standings.data) return null;
    const index = standings.data.standings.findIndex((r) => r.userId === user.id);
    if (index < 0) return null;
    return { ...standings.data.standings[index]!, rank: index + 1 };
  }, [standings.data, user.id]);

  return (
    <Card title="Your stats">
      {!hasGroups ? (
        <p className="text-sm text-[color:var(--color-fg-muted)]">
          Join or create a group to start tracking stats against friends.
        </p>
      ) : standings.isLoading ? (
        <Spinner size="sm" />
      ) : !myRow ? (
        <p className="text-sm text-[color:var(--color-fg-muted)]">
          No scored predictions yet. Your stats appear here once matches finish.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Rank" value={`#${myRow.rank}`} />
          <Stat label="Points" value={String(myRow.totalPoints)} />
          <Stat label="Scored" value={`${myRow.scoredCount} / ${myRow.matchesScored}`} />
          <Stat label="Exact" value={String(myRow.exactCount)} />
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-center rounded-lg bg-[color:var(--color-bg)] p-3">
      <span className="text-[10px] uppercase tracking-wide text-[color:var(--color-fg-muted)]">
        {label}
      </span>
      <span className="text-xl font-bold tabular-nums">{value}</span>
    </div>
  );
}

function DisplayNameCard({ current }: { current: string }) {
  const qc = useQueryClient();
  const [name, setName] = useState(current);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const save = useMutation({
    mutationFn: () => api.put<{ user: UserPublic }>('/api/user/profile', { displayName: name }),
    onSuccess: () => {
      setError(null);
      setOk(true);
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Save failed'),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setOk(false);
    save.mutate();
  }

  return (
    <Card title="Display name">
      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          minLength={1}
          maxLength={64}
          className={inputBase}
        />
        {error ? <p className="text-xs text-[color:var(--color-danger)]">{error}</p> : null}
        {ok ? <p className="text-xs text-[color:var(--color-accent)]">Saved.</p> : null}
        <button
          type="submit"
          disabled={save.isPending || name === current}
          className="h-11 rounded-xl bg-[color:var(--team-primary)] font-medium text-[color:var(--team-on-primary)] disabled:pointer-events-none disabled:opacity-60"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </form>
    </Card>
  );
}

const FAVORITE_TEAM_COOLDOWN_MS = 24 * 60 * 60 * 1000;

function favoriteTeamCooldownHoursLeft(updatedAt: string | null): number | null {
  if (!updatedAt) return null;
  const last = new Date(updatedAt).getTime();
  if (Number.isNaN(last)) return null;
  const remaining = FAVORITE_TEAM_COOLDOWN_MS - (Date.now() - last);
  if (remaining <= 0) return null;
  return Math.ceil(remaining / (60 * 60 * 1000));
}

function FavoriteTeamCard() {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get<TeamsResponse>('/api/teams'),
    staleTime: 60 * 60 * 1000,
  });

  const currentId = user?.favoriteTeamId ?? null;
  const favoriteTeamUpdatedAt = user?.favoriteTeamUpdatedAt ?? null;
  const [confirmOpen, setConfirmOpen] = useState(false);

  const team = useMemo(() => {
    if (!currentId || !teams.data) return null;
    return teams.data.teams.find((t) => t.id === currentId) ?? null;
  }, [currentId, teams.data]);

  const theme = team ? getTeamTheme(team.fifaCode) : null;

  const hoursLeft = favoriteTeamCooldownHoursLeft(favoriteTeamUpdatedAt);
  // Admins bypass the 24h reroll cooldown server-side; mirror that in the UI
  // so the button isn't disabled and the countdown hint doesn't show.
  const onCooldown = hoursLeft !== null && user?.role !== 'admin';

  // Takeover path: the user has a favorite team WITH a registered theme
  // (all 48 WC26 teams have one). Render the handoff's team-card look
  // with flag emoji + national display font + ambient backdrop.
  if (team && theme) {
    return (
      <TeamTakeoverCard
        team={team}
        onReroll={() => setConfirmOpen(true)}
        onCooldown={onCooldown}
        hoursLeft={hoursLeft}
        confirmOpen={confirmOpen}
        onConfirm={() => {
          setConfirmOpen(false);
          navigate('/reroll');
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    );
  }

  // Fallback for the (rare) case of a favorite team without a theme match,
  // or for admin-forced pre-seed states.
  return (
    <Card title="Favorite team">
      {teams.isLoading ? (
        <Spinner size="sm" />
      ) : (
        <div className="flex flex-col gap-3">
          {team ? (
            <div className="flex items-center gap-3">
              {team.flagUrl ? (
                <img
                  src={team.flagUrl}
                  alt=""
                  className="h-6 w-6 shrink-0 rounded-full object-cover"
                />
              ) : (
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-border)] text-[10px] font-bold uppercase text-[color:var(--color-fg-muted)]">
                  {team.fifaCode.slice(0, 2)}
                </span>
              )}
              <div className="flex flex-col">
                <span className="text-sm font-medium">{team.name}</span>
                {team.group ? (
                  <span className="text-xs text-[color:var(--color-fg-muted)]">
                    Group {team.group}
                  </span>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="text-sm text-[color:var(--color-fg-muted)]">None selected.</p>
          )}

          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={onCooldown}
            className={cn(
              'flex h-11 items-center justify-center rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] font-medium',
              'disabled:pointer-events-none disabled:opacity-60',
            )}
          >
            Reroll team
          </button>
          {onCooldown ? (
            <p className="text-xs text-[color:var(--color-fg-muted)]">
              You can reroll again in {hoursLeft}h.
            </p>
          ) : null}
        </div>
      )}
      {confirmOpen ? (
        <ConfirmModal
          title="Reroll your team?"
          body={
            team
              ? `You'll lose ${team.name} as your favorite and the wheel will pick a new one. This can't be undone.`
              : `The wheel will pick a new favorite team. This can't be undone.`
          }
          confirmLabel="Reroll"
          confirmVariant="danger"
          onConfirm={() => {
            setConfirmOpen(false);
            navigate('/reroll');
          }}
          onCancel={() => setConfirmOpen(false)}
        />
      ) : null}
    </Card>
  );
}

/**
 * Themed "You're backing X" card. Mirrors the handoff's Profile card at
 * /tmp/mondy-design/skins/mondy-v2/project/screens.jsx lines 572-595:
 * deep-gradient background, flag emoji, team display font for country name,
 * native-language greeting, scoped TeamBackdrop.
 */
function TeamTakeoverCard({
  team,
  onReroll,
  onCooldown,
  hoursLeft,
  confirmOpen,
  onConfirm,
  onCancel,
}: {
  team: TeamPublic;
  onReroll: () => void;
  onCooldown: boolean;
  hoursLeft: number | null;
  confirmOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const theme = getTeamTheme(team.fifaCode)!;
  const isLight = theme.palette.mode === 'light';
  const cardBase = isLight ? theme.palette.ink ?? '#0b0b0d' : theme.palette.deep;

  return (
    <section
      style={{
        position: 'relative',
        borderRadius: 20,
        padding: '28px 24px',
        background: `linear-gradient(135deg, ${cardBase} 0%, var(--surface) 100%)`,
        border: `1px solid ${theme.palette.primary}55`,
        overflow: 'hidden',
      }}
    >
      <TeamBackdrop theme={theme} intensity={0.35} />
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        {team.flagUrl ? (
          <img
            src={team.flagUrl}
            alt=""
            style={{
              width: 88,
              height: 60,
              objectFit: 'cover',
              borderRadius: 10,
              boxShadow:
                '0 6px 16px rgba(0,0,0,.35), inset 0 0 0 1px rgba(255,255,255,.18)',
              flexShrink: 0,
            }}
            aria-hidden
          />
        ) : (
          <div
            style={{
              fontSize: 72,
              lineHeight: 1,
              filter: 'drop-shadow(0 6px 16px rgba(0,0,0,.35))',
              userSelect: 'none',
              flexShrink: 0,
            }}
            aria-hidden
          >
            {theme.flag}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 10,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--fg-muted)',
              fontWeight: 600,
            }}
          >
            You're backing
          </div>
          <div
            style={{
              fontFamily: 'var(--team-font)',
              fontSize: 40,
              lineHeight: 1,
              color: theme.palette.primary,
              letterSpacing: '0.01em',
              wordBreak: 'break-word',
            }}
          >
            {theme.country}
          </div>
          <div
            style={{
              fontFamily: 'var(--team-font)',
              fontSize: 16,
              color: 'var(--fg-muted)',
              lineHeight: 1.2,
            }}
          >
            {theme.greeting}
          </div>
          {team.group ? (
            <div
              style={{
                fontSize: 11,
                color: 'var(--fg-muted)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: 600,
                marginTop: 2,
              }}
            >
              {`Group ${team.group}`}
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 1, marginTop: 20 }}>
        <button
          type="button"
          onClick={onReroll}
          disabled={onCooldown}
          className={cn(
            'flex h-11 w-full items-center justify-center rounded-xl border font-medium',
            'disabled:pointer-events-none disabled:opacity-60',
          )}
          style={{
            borderColor: `${theme.palette.primary}66`,
            background: 'rgba(0,0,0,0.25)',
            color: 'var(--fg)',
          }}
        >
          Reroll team
        </button>
        {onCooldown ? (
          <p
            style={{
              fontSize: 12,
              color: 'var(--fg-muted)',
              marginTop: 8,
              textAlign: 'center',
            }}
          >
            You can reroll again in {hoursLeft}h.
          </p>
        ) : null}
      </div>
      {confirmOpen ? (
        <ConfirmModal
          title="Reroll your team?"
          body={`You'll lose ${team.name} as your favorite and the wheel will pick a new one. This can't be undone.`}
          confirmLabel="Reroll"
          confirmVariant="danger"
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      ) : null}
    </section>
  );
}

function AdminFavoriteTeamPicker({ currentId }: { currentId: string | null }) {
  const qc = useQueryClient();
  const available = useQuery({
    queryKey: ['user', 'available-teams'],
    queryFn: () =>
      api.get<{ teams: DestinyTeamOption[] }>('/api/user/available-teams'),
    staleTime: 60 * 1000,
  });
  const [selected, setSelected] = useState<string>(currentId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    setSelected(currentId ?? '');
  }, [currentId]);

  const save = useMutation({
    mutationFn: () =>
      api.put<{ user: UserPublic }>('/api/user/favorite-team', {
        teamId: selected,
      }),
    onSuccess: () => {
      setError(null);
      setOk(true);
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      void qc.invalidateQueries({ queryKey: ['user', 'available-teams'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Save failed'),
  });

  const dirty = selected !== '' && selected !== (currentId ?? '');

  return (
    <div className="mt-2 flex flex-col gap-3 border-t border-[color:var(--color-border)] pt-3">
      <p className="text-xs uppercase tracking-wide text-[color:var(--color-fg-muted)]">
        Admin override
      </p>
      <select
        value={selected}
        onChange={(e) => {
          setSelected(e.target.value);
          setOk(false);
        }}
        disabled={available.isLoading}
        className={cn(inputBase, 'appearance-none')}
      >
        <option value="" disabled>
          {available.isLoading ? 'Loading teams…' : 'Choose a team'}
        </option>
        {(available.data?.teams ?? []).map(({ team: t }) => (
          <option key={t.id} value={t.id}>
            {t.name}
            {t.group ? ` — Group ${t.group}` : ''}
          </option>
        ))}
      </select>
      {error ? <p className="text-xs text-[color:var(--color-danger)]">{error}</p> : null}
      {ok ? <p className="text-xs text-[color:var(--color-accent)]">Saved.</p> : null}
      <button
        type="button"
        disabled={!dirty || save.isPending}
        onClick={() => save.mutate()}
        className="h-11 rounded-xl bg-[color:var(--color-accent)] font-medium text-[color:var(--color-accent-fg)] disabled:pointer-events-none disabled:opacity-60"
      >
        {save.isPending ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

function RemindersCard({
  enabled,
  currentTimezone,
}: {
  enabled: boolean;
  currentTimezone: string;
}) {
  const qc = useQueryClient();
  const [on, setOn] = useState(enabled);
  const [tz, setTz] = useState(currentTimezone);
  const [error, setError] = useState<string | null>(null);

  // Keep the user's browser tz in the list even if it's not in COMMON_TIMEZONES.
  const browserTz = localTimezone();
  const tzOptions = useMemo(() => {
    const set = new Set(COMMON_TIMEZONES);
    set.add(browserTz);
    if (currentTimezone) set.add(currentTimezone);
    return Array.from(set).sort();
  }, [browserTz, currentTimezone]);

  const save = useMutation({
    mutationFn: () =>
      api.put<{ user: UserPublic }>('/api/user/reminders', {
        enabled: on,
        timezone: tz,
      }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Save failed'),
  });

  const dirty = on !== enabled || tz !== currentTimezone;

  return (
    <Card
      title="Email reminders"
      description="We’ll email you about 30 minutes before each match kicks off, rendered in the timezone below."
    >
      <div className="flex flex-col gap-3">
        <label className="flex items-center justify-between gap-3">
          <span className="text-sm">Send reminders</span>
          <button
            type="button"
            role="switch"
            aria-checked={on}
            onClick={() => setOn((v) => !v)}
            className={cn(
              'h-7 w-12 rounded-full transition',
              on ? 'bg-[color:var(--color-accent)]' : 'bg-[color:var(--color-bg)]',
            )}
          >
            <span
              className={cn(
                'block h-6 w-6 rounded-full bg-white transition',
                on ? 'translate-x-[22px]' : 'translate-x-0.5',
              )}
            />
          </button>
        </label>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="tz"
            className="text-xs uppercase tracking-wide text-[color:var(--color-fg-muted)]"
          >
            Timezone
          </label>
          <select
            id="tz"
            value={tz}
            onChange={(e) => setTz(e.target.value)}
            className={cn(inputBase, 'appearance-none')}
          >
            {tzOptions.map((z) => (
              <option key={z} value={z}>
                {z}
                {z === browserTz ? ' (browser)' : ''}
              </option>
            ))}
          </select>
        </div>
        {error ? <p className="text-xs text-[color:var(--color-danger)]">{error}</p> : null}
        <button
          type="button"
          disabled={save.isPending || !dirty}
          onClick={() => save.mutate()}
          className="h-11 rounded-xl bg-[color:var(--color-accent)] font-medium text-[color:var(--color-accent-fg)] disabled:pointer-events-none disabled:opacity-60"
        >
          {save.isPending ? 'Saving…' : 'Save reminders'}
        </button>
      </div>
    </Card>
  );
}
